import { useQuery } from '@tanstack/react-query';
import { globalSearch } from '@/api/globalSearchService';
import { useUserStore } from '@/stores';
import type { GlobalSearchResults } from '@/types/globalSearch';

/**
 * Searches across permitted modules in parallel.
 * Only fires requests for modules the user has permission to access.
 * Enabled only when query has 2+ characters.
 * Results are cached per query + permissions, kept for 30s.
 */
/**
 * @param full  The dropdown wants a short preview; the "View all results" page
 *              wants the page. `full` is part of the QUERY KEY, so the two
 *              cannot share a cache entry — without that, whichever mounted
 *              first would serve its result to the other and the page would
 *              show five rows again from cache. (BOXY-234.)
 */
export function useGlobalSearch(query: string, full = false): GlobalSearchResults {
  const trimmed = query.trim();
  const enabled = trimmed.length >= 2;

  const user = useUserStore((state) => state.user);
  const permissions = (user?.permissions ?? []) as string[];

  const { data, isFetching } = useQuery(
    ['global-search', trimmed, permissions, full],
    () => globalSearch(trimmed, permissions, full),
    {
      enabled,
      keepPreviousData: true,
      staleTime: 30_000,
      cacheTime: 60_000,
      retry: false,
    },
  );

  const groups = data ?? [];
  // `totalResults` gates the dropdown's show-results vs. empty-state. A module can
  // return renderable `results` while reporting `total: 0` (unreliable Meili
  // search envelope), and `0 ?? results.length` keeps the bogus 0 — wrongly
  // showing "No results found". Never let the count fall below what we render.
  const totalResults = groups.reduce(
    (sum, g) => sum + Math.max(g.total ?? 0, g.results.length),
    0,
  );

  return {
    groups,
    isLoading: enabled && isFetching,
    totalResults,
  };
}
